import { type ElementType } from 'react'
import { Box } from '../../primitives/Box'
import { BareButton } from '../../primitives/Button'
import { ListItemSecondaryAction } from './inner/ListItemSecondaryAction'
import { type ListItemProps } from './types'
import { useStyle } from './use-style'
import { ListItemFooter } from './ListItemFooter'
import { ListItemLabel } from './ListItemLabel'

/**
 * Represent a `ListItem` as part of the `List` or any element by itself. You have the option to place an icon/illustration at the left.
 * An arrow or secondary action at the right, making it a button or selectable, disabled state. Even expandable or multiple actions with the `footer` slot or secondaryAction slot.
 *
 * Demos:
 * - [ListItem](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/constructs-listitem--docs)
 *
 */
export const ListItem = ({
  onClick,
  alignItems = 'center',
  autoFocus,
  children,
  Component,
  slotProps = {},
  background,
  hasDivider = true,
  isButton = false,
  isDense = false,
  isDisabled = false,
  isRounded = false,
  isSelected = false,
  isVertical = false,
  ListComponent = 'li',
  to,
  variant,
  sx,
  ...rest
}: ListItemProps) => {
  const { footer, line, secondaryAction, secondaryAlign, secondaryIcon, hasDividerFooter } = slotProps

  // reason: not needed for abstracting in much complexity
  // eslint-disable-next-line no-nested-ternary
  const Item: ElementType = Component || (isButton ? (variant === 'selectable' ? ListItemLabel : BareButton) : Box)

  const py = isDense ? 0.75 : 2
  const align = secondaryAlign || (secondaryAction ? 'flex-start' : 'center')

  const styles = useStyle({
    onClick,
    alignItems,
    isButton,
    isSelected,
    isDisabled,
    background,
    isRounded,
    isVertical,
    line,
    py,
    secondaryAction,
    secondaryIcon,
    sx,
    to,
    variant,
  })

  return (
    <Box
      className="DhlListItem"
      component={ListComponent}
      sx={{
        position: 'relative',
        flex: '1 1 auto',

        ...(!isRounded &&
          hasDivider && {
            [isVertical ? 'borderRight' : 'borderBottom']: '1px solid',
            borderColor: 'divider',
          }),

        ...(isDisabled && { '.DhlListItem-illustration': { filter: 'grayscale(1)' } }),
        ...(isVertical && {
          '.DhlListItem-illustration, .DhlListItem-icon, .DhlListItem-illustration': {
            mx: 'auto',
            paddingRight: 0,
            mb: 2,
          },
        }),
      }}
    >
      <Box
        className={`DhlListItem-item ${isVertical ? 'DhlListItem--vertical' : ''} ${isRounded ? 'DhlListItem-rounded' : ''}`}
        component={Item}
        to={to}
        sx={styles}
        disabled={isButton ? isDisabled : undefined}
        onClick={onClick}
        autoFocus={autoFocus}
        {...rest}
      >
        {children}

        {
          // See the prop, "secondaryAction" we explain there to not use both Action and Icon then we let Action win :)
          !secondaryAction && secondaryIcon && (
            <ListItemSecondaryAction
              py={py}
              isDisabled={isDisabled}
              secondaryAlign={align}
              secondaryIcon={secondaryIcon}
            />
          )
        }

        {!footer && line && (
          <ListItemFooter className="DhlListItem-line" isInline hasDivider={hasDividerFooter}>
            {line}
          </ListItemFooter>
        )}
      </Box>

      {
        // See the prop, "secondaryAction" we explain there to not use both Action and Icon then we let Action win :)
        secondaryAction && (
          <ListItemSecondaryAction
            py={py}
            isDisabled={isDisabled}
            secondaryAlign={align}
            secondaryAction={secondaryAction}
          />
        )
      }

      {footer && (
        <ListItemFooter isSelected={isSelected} background={background} hasDivider={hasDividerFooter}>
          {footer}
        </ListItemFooter>
      )}
    </Box>
  )
}
